function line(label, value) {
  if (value === null || value === undefined || value === '') return null;
  return `${label}: ${value}`;
}

function o2WithUnit(value) {
  if (value == null || value === '') return null;
  const n = String(value).replace(/%+\s*$/g, '').trim();
  return n ? `${n}%` : null;
}

function patientHeader(patient, datetime) {
  const lines = ['Sawtna'];
  if (patient?.name) lines.push(`Patient: ${patient.name}`);
  if (patient?.room) lines.push(`Room: ${patient.room}`);
  if (patient?.age != null) lines.push(`Age: ${patient.age}`);
  if (patient?.sex) lines.push(`Sex: ${patient.sex}`);
  if (datetime) {
    const dateStr = new Date(datetime).toLocaleString('en-JO', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
    lines.push(`Date: ${dateStr}`);
  }
  return lines.join('\n');
}

export function formatSoapNote(note, datetime) {
  const patient = note?.patient;
  const narrative = typeof note?.narrative === 'string' ? note.narrative.trim() : '';
  if (narrative) {
    return `${patientHeader(patient, datetime || note?.datetime)}\nNote Type: Progress Note\n\n${narrative}`;
  }

  const soap = note?.soap || {};
  const sections = [
    ['S — Subjective', soap.subjective],
    ['O — Objective', soap.objective],
    ['A — Assessment', soap.assessment],
    ['P — Plan', soap.plan],
  ];

  const body = sections
    .map(([title, content]) => `${title}\n${content || '—'}`)
    .join('\n\n');

  return `${patientHeader(patient, datetime || note?.datetime)}\nNote Type: Progress Note\n\n${body}`;
}

export function formatExtractedVitals(vitals) {
  if (!vitals) return '';

  const rows = [
    line('BP', vitals.bp),
    line('Pulse', vitals.pulse != null ? `${vitals.pulse} bpm` : null),
    line('Temp', vitals.temp_celsius != null ? `${vitals.temp_celsius}°C` : null),
    line('O2 Sat', o2WithUnit(vitals.o2_sat)),
    line('RR', vitals.rr != null ? `${vitals.rr}/min` : null),
    line('Pain', vitals.pain != null ? `${vitals.pain}/10` : null),
  ].filter(Boolean);

  if (!rows.length) return 'No vitals recorded.';

  return `Vitals\n${rows.join('\n')}`;
}

export function formatSbarNote(note, datetime) {
  const sbar = note?.sbar || {};
  const patient = note?.patient;
  const narrative =
    (typeof note?.narrative === 'string' && note.narrative.trim())
    || [sbar.situation, sbar.background, sbar.assessment, sbar.recommendation]
      .map((value) => String(value || '').trim())
      .filter(Boolean)
      .join('\n\n');

  let body = narrative || '—';

  const tasks = note?.outstanding_tasks || [];
  if (tasks.length) {
    const taskLines = tasks
      .map((task) => {
        const text = typeof task === 'string' ? task : task.text;
        const done = typeof task === 'object' && task.completed;
        return `${done ? '[x]' : '[ ]'} ${text}`;
      })
      .join('\n');
    body += `\n\nOutstanding Tasks\n${taskLines}`;
  }

  return `${patientHeader(patient, datetime || note?.datetime)}\nNote Type: Handover / Shift Report\n\n${body}`;
}

export function formatVitalsNote(note, datetime) {
  const vitals = note?.vitals || {};
  const patient = note?.patient;

  const rows = [
    vitals.bp_systolic != null && vitals.bp_diastolic != null
      ? `BP: ${vitals.bp_systolic}/${vitals.bp_diastolic} mmHg`
      : null,
    line('Pulse', vitals.pulse != null ? `${vitals.pulse} bpm` : null),
    line('Temp', vitals.temp_celsius != null ? `${vitals.temp_celsius}°C` : null),
    line('O2 Sat', o2WithUnit(vitals.o2_sat)),
    line('RR', vitals.rr != null ? `${vitals.rr}/min` : null),
    line('Pain', vitals.pain != null ? `${vitals.pain}/10` : null),
    line('Weight', vitals.weight_kg != null ? `${vitals.weight_kg} kg` : null),
    line('Blood Sugar', vitals.blood_sugar != null ? `${vitals.blood_sugar} mg/dL` : null),
  ].filter(Boolean);

  const body = rows.length ? rows.join('\n') : 'No vitals recorded.';

  return `${patientHeader(patient, datetime || note?.datetime)}\nNote Type: Vitals\n\n${body}`;
}

export function formatCortiSections(sections, patient, datetime) {
  const header = patient
    ? `${patientHeader(patient, datetime)}\nNote Type: Clinical Note (Corti)\n\n`
    : '';

  const body = (sections || [])
    .map((s) => `${s.title}\n${s.content || '—'}`)
    .join('\n\n');

  return header + body;
}
